export * from './leadRouting/index'

export const SCHEDULED_FALLBACK_ASSIGNMENT_STEP =
  'scheduled_fallback_assignment'

export enum LeadRoutingMessageType {
  ROUTE_LEAD = 'ROUTE_LEAD',
  CHECK_UNASSIGNED_LEAD = 'CHECK_UNASSIGNED_LEAD',
  RECIRCULATE_LEAD = 'RECIRCULATE_LEAD',
  DIGITAL_SIGNAL = 'DIGITAL_SIGNAL',
}

export interface LeadRoutingMessage {
  messageType?: LeadRoutingMessageType
  applicationId: number
  applicationRevision: object
  state: string
  leadSource: string
  traceUuid: string
  loanOfficerIdToOffer?: number
  assignmentPolicy?: string
  offerIdToExpire?: number
  leadRoutingId?: number
  scheduledAt?: Date
  recirculationId?: number
  digitalSignal?: DigitalSignal
}

// Events, not statuses: every occurrence is sent. Each one that reaches a
// decision leaves a digital_signal_events row.
export enum DigitalSignal {
  PrePricingRatesDisplayed = 'prePricingRatesDisplayed',
  PrePricingNoRatesDisplayed = 'prePricingNoRatesDisplayed',
  AccountLinkingTouched = 'accountLinkingTouched',
  AlfieCompleted = 'alfieCompleted',
  BorrowerPortalRatesDisplayed = 'borrowerPortalRatesDisplayed',
}

export enum DigitalSignalMode {
  Off = 'off',
  Shadow = 'shadow',
  Run = 'run',
}

// Which of the two actions a signal was decided for.
export enum DigitalSignalEventPath {
  SqueezeReengage = 'squeeze_reengage',
  LoanOfficerDm = 'lo_dm',
}

export type DigitalSignalResult =
  | 'routed'
  | 'shadow'
  | 'unsqueezed'
  | 'skipped_flag_off'
  | 'skipped_dnc'
  | 'skipped_phoenix'
  | 'skipped_not_in_squeeze'
  | 'error'

export type DigitalSignalNotificationResult =
  | 'notified'
  | 'shadow'
  | 'skipped_notified_by_source'
  | 'skipped_no_lo'
  | 'skipped_reoffer_pending'
  | 'skipped_cooldown'
  | 'error'

export enum RecirculationSignal {
  Dial2 = 'dial_2',
  Dial3 = 'dial_3',
  Dial4 = 'dial_4',
  Dial5 = 'dial_5',
  Squeeze = 'squeeze',
}

export enum RecirculationOutcome {
  Reassigned = 'reassigned',
  NoLoanOfficer = 'no_loan_officer',
  Squeezed = 'squeezed',
  Shadow = 'shadow',
}

export enum RecirculationRemovalReason {
  FeatureDisabled = 'feature_disabled',
  ExternallyManaged = 'externally_managed',
  PhoenixManaged = 'phoenix_managed',
  Withdrawn = 'withdrawn',
  ManuallyReassigned = 'manually_reassigned',
  ProtectedLead = 'protected_lead',
  InboundContact = 'inbound_contact',
  PriorContact = 'prior_contact',
  RateLocked = 'rate_locked',
  VepaApproved = 'vepa_approved',
  TridTripped = 'trid_tripped',
  ConnectedCall = 'connected_call',
  ConnectedCallLive = 'connected_call_live',
}

export enum RecirculationDeferReason {
  CallInProgress = 'call_in_progress',
  OutsideContactHours = 'outside_contact_hours',
  SqueezeSendFailed = 'squeeze_send_failed',
}

export enum RecirculationMode {
  Off = 'off',
  Shadow = 'shadow',
  Run = 'run',
}

// `until` is ISO 8601. `shadow` replaces the two LO-facing actions in shadow
// mode; stops and defers are returned as themselves.
export type RecirculationDecision =
  | { action: 'stop'; reason: RecirculationRemovalReason }
  | { action: 'defer'; reason: RecirculationDeferReason; until: string }
  | {
      action: 'offer'
      holderLoanOfficerId: number | null
      currentAssignmentId: number | null
    }
  | { action: 'squeezed' }
  | { action: 'shadow'; wouldHave: 'offer' | 'squeezed' }

export interface LeadRoutingOfferEvent {
  applicationId: number
  loanOfficerId: number
  status: 'offered' | 'accepted' | 'declined' | 'expired'
  offeredAt?: Date
  expiresAt?: Date
  respondedAt?: Date
}

export enum LeadRoutingStatus {
  Offered = 'offered',
  Declined = 'declined',
  Accepted = 'accepted',
  Expired = 'expired',
}
